import React from "react";
import { Tooltip } from "@mui/material";

const DEFAULT_DISABLED_REASON =
  "You must confirm your email address before creating or editing locations.";

interface EmailVerifyGuardTooltipProps {
  /** When `true`, the guard is active: the tooltip shows and the wrapped control is treated as disabled. */
  disabled: boolean;
  /** Message shown in the tooltip while disabled. Defaults to the standard email-confirmation reason. */
  disabledReason?: string;
  /** The control to guard — typically an MUI `Button`. */
  children: React.ReactElement<any>;
}

/**
 * Wraps an interactive control (e.g. a `Button`) so that, while `disabled` is
 * `true`, a tooltip explains why the action is unavailable.
 *
 * MUI suppresses pointer events on disabled elements, so the control is wrapped
 * in a `<span>` — without it the tooltip would never fire on hover/focus.
 * When `disabled` is `false` the children render unchanged (no tooltip, no span
 * styling side-effects beyond an inline-flex wrapper).
 *
 * @example
 * <EmailVerifyGuardTooltip disabled={!isEmailVerified}>
 *   <Button onClick={openCreateModal}>Create Another Location</Button>
 * </EmailVerifyGuardTooltip>
 */
const EmailVerifyGuardTooltip = ({
  disabled,
  disabledReason = DEFAULT_DISABLED_REASON,
  children,
}: EmailVerifyGuardTooltipProps) => {
  if (!disabled) {
    return children;
  }

  // Clone to force the disabled state + a11y attribute onto the control itself.
  const guardedChild = React.cloneElement(children, {
    disabled: true,
    "aria-disabled": true,
  });

  return (
    <Tooltip title={disabledReason} arrow>
      {/* span keeps hover/focus events alive over the disabled control */}
      <span style={{ display: "inline-flex" }}>{guardedChild}</span>
    </Tooltip>
  );
};

export default EmailVerifyGuardTooltip;

